import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Globe, Mail, MapPin, CheckCircle, UserPlus, MessageCircle } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { traderService, type TraderFeatures } from '../services/traderService';
import craftsmanService, { type Craftsman } from '../services/craftsmanService';
import Footer from '../components/Footer';
import SocialLinksList from '../components/SocialLinksList';
import FeatureShowcase from '../components/FeatureShowcase';

type CraftieDetail = Craftsman & {
  profession?: Craftsman['craft_professions'];
  mockup?: boolean;
};

const CraftieDetailPage: React.FC = () => {
  const { slug: slugOrId } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [craftie, setCraftie] = useState<CraftieDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [craftieFeatures, setCraftieFeatures] = useState<TraderFeatures | null>(null);

  useEffect(() => {
    const fetchCraftie = async () => {
      if (!slugOrId) {
        setError('No craftie identifier provided');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        
        let craftieData = await craftsmanService.getCraftsmanBySlug(slugOrId);
        let fetchedById = false;

        if (!craftieData) {
          craftieData = await craftsmanService.getCraftsmanById(slugOrId);
          fetchedById = Boolean(craftieData);
        }

        if (!craftieData) {
          throw new Error('Craftie not found');
        }

        if (fetchedById && craftieData.slug && craftieData.slug !== slugOrId) {
          navigate(`/craftsman/${craftieData.slug}`, { replace: true });
          return;
        }

        setCraftie({
          ...craftieData,
          profession: craftieData.craft_professions
        });
      } catch (err) {
        console.error('Error fetching craftie:', err);
        setError('Failed to load craftie details');
      } finally {
        setLoading(false);
      }
    };

    fetchCraftie();
  }, [slugOrId, navigate]);

  useEffect(() => {
    const fetchFeatures = async () => {
      if (!craftie?.user_id) return;

      try {
        const features = await traderService.getTraderFeatures(craftie.user_id);
        setCraftieFeatures(features);
      } catch (err) {
        console.error('Error fetching craftie features:', err);
        setCraftieFeatures(null);
      }
    };

    fetchFeatures();
  }, [craftie?.user_id]);

  const handleBack = () => {
    navigate(-1);
  };

  // Build a list of enabled features to render as cards on the detail page
  // If no features exist in database, create default feature based on craftie data
  const enabledFeatures = React.useMemo(
    () => {
      const fallbackImage = craftie?.avatar_url || '/bally-Market-sign-200px.png';

      // If no features found in database, create default feature from craftie data
      if (!craftieFeatures) {
        return [] as Array<{ title: string; description: string; image: string }>;
      }

      const features = [
        {
          title: craftieFeatures.feature1_title || 'Feature 1',
          description: craftieFeatures.feature1_description || '',
          image: craftieFeatures.feature1_image || fallbackImage,
          enabled: craftieFeatures.feature1_enabled
        },
        {
          title: craftieFeatures.feature2_title || 'Feature 2',
          description: craftieFeatures.feature2_description || '',
          image: craftieFeatures.feature2_image || fallbackImage,
          enabled: craftieFeatures.feature2_enabled
        },
        {
          title: craftieFeatures.feature3_title || 'Feature 3',
          description: craftieFeatures.feature3_description || '',
          image: craftieFeatures.feature3_image || fallbackImage,
          enabled: craftieFeatures.feature3_enabled
        }
      ].filter(feature => {
        const enabledValue = feature.enabled as any;
        const isEnabled =
          enabledValue === true ||
          enabledValue === 'true' ||
          enabledValue === '1' ||
          enabledValue === 1;

        return isEnabled;
      });

      return features as Array<{ title: string; description: string; image: string }>;
    },
    [craftieFeatures, craftie?.avatar_url, craftie?.store_name, craftie?.owner_name, craftie?.description]
  );

  const hasLocation = Boolean(craftie?.location && craftie.location.trim().length > 0);
  const shouldShowMap = craftie ? (((craftie as any).show_map ?? true) as boolean) : false;
  const mapUrl = hasLocation && shouldShowMap && craftie
    ? `https://www.google.com/maps?q=${encodeURIComponent(`${craftie.location}, Ireland`)}&output=embed`
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6 mt-16">
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-48" />
                  <Skeleton className="h-6 w-6 rounded-full" />
                </div>
                <Skeleton className="h-6 w-32" />
                <div className="space-y-3">
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-3/4" />
                </div>
              </div>
              <div className="flex justify-center">
                <Skeleton className="w-full max-w-sm aspect-square rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !craftie) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Craftie Not Found</h2>
            <p className="text-gray-600 mb-6">
              {error || "The craftie you're looking for doesn't exist or may have been removed."}
            </p>
            <button
              onClick={handleBack}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 mt-16"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to results
        </button>

        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-8 space-y-8">
            <div className="flex flex-col gap-8 lg:grid lg:grid-cols-2 lg:gap-10">
              {/* Business Name & Category */}
              <section className="space-y-3 lg:col-span-2">
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {craftie.store_name || craftie.owner_name}
                  </h1>
                  {craftie.verified && (
                    <CheckCircle className="w-6 h-6 text-blue-500" />
                  )}
                </div>
                <p className="text-sm uppercase tracking-wide text-gray-500">
                  {craftie.profession?.name || 'Craftsperson'}
                </p>
              </section>

              {/* Image / Avatar */}
              <section>
                <div className="w-full max-w-sm mx-auto aspect-square rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                  {craftie.avatar_url ? (
                    <img
                      src={craftie.avatar_url}
                      alt={craftie.store_name || craftie.owner_name}
                      className="w-full h-full object-cover object-center"
                    />
                  ) : (
                    <img
                      src={`https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(craftie.store_name || craftie.owner_name)}&backgroundColor=f3f4f6&fontSize=40`}
                      alt={craftie.store_name || craftie.owner_name}
                      className="w-full h-full object-cover object-center"
                    />
                  )}
                </div>
              </section>

              {/* Contact Details */}
              <section className="space-y-4">
                {craftie.phone_number && (
                  <p className="flex items-center text-gray-700">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium mr-2">Phone:</span>
                    <a 
                      href={`tel:${craftie.phone_number}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {craftie.phone_number}
                    </a>
                  </p>
                )}
                
{/* Only show email if it's not a fake placeholder */}
                {craftie.email && !craftie.email.endsWith('@unclaimed.ballymarket.local') && (
                  <p className="flex items-center text-gray-700">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium mr-2">Email:</span>
                    <a 
                      href={`mailto:${craftie.email}`}
                      className="text-blue-600 hover:text-blue-800 truncate"
                    >
                      {craftie.email}
                    </a>
                  </p>
                )}
                
                {craftie.website && (
                  <p className="flex items-center text-gray-700">
                    <Globe className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium mr-2">Website:</span>
                    <a 
                      href={craftie.website.startsWith('http') ? craftie.website : `https://${craftie.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 truncate"
                    >
                      {craftie.website}
                    </a>
                  </p>
                )}

                <SocialLinksList userId={craftie.user_id} className="mt-2" />
                
                <p className="flex items-center text-gray-700">
                  <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="font-medium mr-2">Location:</span>
                  {craftie.location}
                </p>
              </section>
            </div>

            {/* About Section */}
            <section className="pt-4 border-t border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">About</h2>
              <p className="text-gray-700 whitespace-pre-wrap">
                {craftie.description || 'No description available.'}
              </p>
            </section>

            {/* Featured Offerings Section */}
            {enabledFeatures.length > 0 && (
              <section className="pt-4 border-gray-200 border-t">
                <FeatureShowcase
                  title={craftieFeatures?.features_section_title || 'Featured Offerings'}
                  features={enabledFeatures}
                  layout={craftieFeatures?.features_layout || 'rows'}
                />
              </section>
            )}

            {/* Map Section */}
            {mapUrl && (
              <section className="pt-4 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Location Map</h3>
                <div className="w-full overflow-hidden rounded-lg border border-gray-200">
                  <iframe
                    title={`Map for ${craftie.store_name || craftie.owner_name}`}
                    src={mapUrl}
                    width="100%"
                    height="260"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </section>
            )}

            {/* Claim Your Business Section - Only show for unclaimed businesses */}
            {craftie.mockup && !craftie.user_id && (
              <section className="pt-4 border-t border-gray-200">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 text-center">
                  {craftie.email?.endsWith('@unclaimed.ballymarket.local') ? (
                    // Business has fake email - show contact us message
                    <>
                      <MessageCircle className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Is this your business?
                      </h2>
                      <p className="text-gray-600 mb-4 max-w-md mx-auto">
                        We don't have contact details for this business on file. 
                        If you're the owner, please contact us to verify your ownership and claim this listing.
                      </p>
                      <a
                        href="mailto:info@ballymarket.ie?subject=Business%20Claim%20Request" 
                        className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Mail className="w-5 h-5 mr-2" />
                        Contact Us to Claim
                      </a>
                      <p className="text-sm text-gray-500 mt-3">
                        Email us at <strong>info@ballymarket.ie</strong> with proof of ownership
                      </p>
                    </>
                  ) : (
                    // Business has real email - show claim button
                    <>
                      <UserPlus className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Is this your business?
                      </h2>
                      <p className="text-gray-600 mb-4 max-w-md mx-auto">
                        Claim this listing to get full access to your business profile. 
                        You'll be able to update your information, add photos, and manage your presence on BallyMarket.
                      </p>
                      <button
                        onClick={() => navigate(`/claim/${craftie.id}`)}
                        className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <UserPlus className="w-5 h-5 mr-2" />
                        Claim Your Business
                      </button>
                      {(craftie.email?.toLowerCase().endsWith('@gmail.com') || craftie.email?.toLowerCase().endsWith('@googlemail.com')) && (
                        <p className="text-sm text-gray-500 mt-3">
                          You can sign up with your Google account ({craftie.email})
                        </p>
                      )}
                    </>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CraftieDetailPage;
